/**
 * HoneyDo Platform - Frontend Role Label Mapper
 *
 * Frontend version of the backend roleMapper utility.
 * Maps generic user_type values to relationship-specific labels for UI display.
 *
 * This is a streamlined version focused on label display only.
 * Permission checks should be handled by the backend.
 *
 * @module roleLabelMapper
 */

/**
 * Role label mappings for all 14 relationship types.
 * Simplified frontend version - only includes labels, not permissions.
 */
const ROLE_LABEL_MAPPINGS = {
  // Household & Living Arrangements
  nuclear_family: {
    parent: 'Parent',
    teen: 'Teen',
    child: 'Child',
    senior: 'Grandparent'
  },
  couples_partners: {
    parent: 'Partner',
    teen: 'Partner',
    child: 'Helper',
    senior: 'Family Member'
  },
  roommates: {
    parent: 'Primary Leaseholder',
    teen: 'Roommate',
    child: 'Subtenant',
    senior: 'Guest'
  },
  tenant_landlord: {
    parent: 'Landlord',
    teen: 'Property Manager',
    child: 'Tenant',
    senior: 'Maintenance Staff'
  },
  vacation_home: {
    parent: 'Owner',
    teen: 'Property Manager',
    child: 'Guest',
    senior: 'Caretaker'
  },

  // Real Estate & Construction
  builder_buyer: {
    parent: 'Builder/Contractor',
    teen: 'Project Manager',
    child: 'Buyer',
    senior: 'Inspector'
  },
  renovation_project: {
    parent: 'Homeowner',
    teen: 'Contractor',
    child: 'Assistant',
    senior: 'Consultant'
  },

  // Community & Social Groups
  sports_team: {
    parent: 'Coach',
    teen: 'Team Captain',
    child: 'Player',
    senior: 'Parent Volunteer'
  },
  performance_group: {
    parent: 'Director',
    teen: 'Lead Performer',
    child: 'Performer',
    senior: 'Stage Manager'
  },
  group_trip: {
    parent: 'Trip Organizer',
    teen: 'Co-Organizer',
    child: 'Participant',
    senior: 'Advisor'
  },
  event_planning: {
    parent: 'Event Coordinator',
    teen: 'Assistant Coordinator',
    child: 'Volunteer',
    senior: 'Advisor'
  },

  // Professional & Business
  small_business: {
    parent: 'Owner',
    teen: 'Manager',
    child: 'Employee',
    senior: 'Consultant'
  },
  hoa_community: {
    parent: 'HOA Board Member',
    teen: 'Committee Member',
    child: 'Resident',
    senior: 'Advisory Board'
  },
  cooperative: {
    parent: 'Board Member',
    teen: 'Coordinator',
    child: 'Member',
    senior: 'Advisor'
  }
};

/**
 * Get contextual role label for a user based on relationship type
 *
 * @param {string} relationshipType - The relationship_type (e.g., 'sports_team')
 * @param {string} userType - The user_type (e.g., 'parent', 'teen', 'child', 'senior')
 * @returns {string} Contextual role label
 *
 * @example
 * getRoleLabel('sports_team', 'parent') // Returns: "Coach"
 * getRoleLabel('nuclear_family', 'child') // Returns: "Child"
 * getRoleLabel('event_planning', 'teen') // Returns: "Assistant Coordinator"
 */
export function getRoleLabel(relationshipType, userType) {
  // Validate inputs
  if (!relationshipType || !userType) {
    console.warn(`getRoleLabel: missing parameters - relationshipType=${relationshipType}, userType=${userType}`);
    return userType || 'Member';
  }

  const mapping = ROLE_LABEL_MAPPINGS[relationshipType];

  if (!mapping) {
    console.warn(`getRoleLabel: unknown relationship type "${relationshipType}", falling back to nuclear_family`);
    return ROLE_LABEL_MAPPINGS.nuclear_family[userType] || userType;
  }

  if (!mapping[userType]) {
    console.warn(`getRoleLabel: unknown user type "${userType}" for relationship "${relationshipType}"`);
    return userType;
  }

  return mapping[userType];
}

/**
 * Get plural form of role label (useful for group displays)
 *
 * @param {string} relationshipType - The relationship_type
 * @param {string} userType - The user_type
 * @returns {string} Plural role label
 *
 * @example
 * getRoleLabelPlural('sports_team', 'child') // Returns: "Players"
 * getRoleLabelPlural('nuclear_family', 'parent') // Returns: "Parents"
 */
export function getRoleLabelPlural(relationshipType, userType) {
  const label = getRoleLabel(relationshipType, userType);

  // Special plural cases
  const specialPlurals = {
    'Child': 'Children',
    'Family Member': 'Family Members',
    'Staff': 'Staff',
    'Advisory Board': 'Advisory Board'
  };

  if (specialPlurals[label]) {
    return specialPlurals[label];
  }

  // Default: add 's'
  return `${label}s`;
}

/**
 * Get all role labels for a specific relationship type
 * Useful for displaying role options during member invitation
 *
 * @param {string} relationshipType - The relationship_type
 * @returns {Object} Object mapping user_type to role label
 *
 * @example
 * getRelationshipRoles('sports_team')
 * // Returns: { parent: 'Coach', teen: 'Team Captain', child: 'Player', senior: 'Parent Volunteer' }
 */
export function getRelationshipRoles(relationshipType) {
  return ROLE_LABEL_MAPPINGS[relationshipType] || ROLE_LABEL_MAPPINGS.nuclear_family;
}

/**
 * Get all available relationship types
 *
 * @returns {string[]} Array of relationship type keys
 */
export function getAllRelationshipTypes() {
  return Object.keys(ROLE_LABEL_MAPPINGS);
}

/**
 * Check if a relationship type is valid
 *
 * @param {string} relationshipType - The relationship_type to check
 * @returns {boolean} True if valid
 */
export function isValidRelationshipType(relationshipType) {
  return ROLE_LABEL_MAPPINGS.hasOwnProperty(relationshipType);
}

/**
 * Get human-readable relationship type name
 *
 * @param {string} relationshipType - The relationship_type key
 * @returns {string} Human-readable name
 *
 * @example
 * getRelationshipTypeName('sports_team') // Returns: "Sports Team"
 * getRelationshipTypeName('nuclear_family') // Returns: "Nuclear Family"
 */
export function getRelationshipTypeName(relationshipType) {
  const nameMap = {
    nuclear_family: 'Nuclear Family',
    couples_partners: 'Couple/Partners',
    roommates: 'Roommates',
    tenant_landlord: 'Tenant/Landlord',
    vacation_home: 'Vacation Home',
    builder_buyer: 'Builder/Buyer',
    renovation_project: 'Renovation Project',
    sports_team: 'Sports Team',
    performance_group: 'Performance Group',
    group_trip: 'Group Trip',
    event_planning: 'Event Planning',
    small_business: 'Small Business',
    hoa_community: 'HOA Community',
    cooperative: 'Cooperative'
  };

  return nameMap[relationshipType] || relationshipType;
}

/**
 * Enrich a user object with contextual role label
 * Adds role_label field to user object
 *
 * @param {Object} user - User object with user_type field
 * @param {string} relationshipType - The relationship_type
 * @returns {Object} User object with added role_label field
 *
 * @example
 * const user = { user_id: '123', user_type: 'parent', display_name: 'John' };
 * enrichUserWithRoleLabel(user, 'sports_team');
 * // Returns: { ...user, role_label: 'Coach' }
 */
export function enrichUserWithRoleLabel(user, relationshipType) {
  if (!user || !user.user_type) {
    console.warn('enrichUserWithRoleLabel: invalid user object');
    return user;
  }

  return {
    ...user,
    role_label: getRoleLabel(relationshipType, user.user_type)
  };
}

/**
 * Format role label with user name for display
 *
 * @param {string} userName - User's display name
 * @param {string} relationshipType - The relationship_type
 * @param {string} userType - The user_type
 * @returns {string} Formatted string like "John (Coach)"
 *
 * @example
 * formatUserWithRole('John', 'sports_team', 'parent') // Returns: "John (Coach)"
 * formatUserWithRole('Sarah', 'nuclear_family', 'teen') // Returns: "Sarah (Teen)"
 */
export function formatUserWithRole(userName, relationshipType, userType) {
  const roleLabel = getRoleLabel(relationshipType, userType);
  return `${userName} (${roleLabel})`;
}

// Export mappings for reference (read-only)
export { ROLE_LABEL_MAPPINGS };

// Default export
export default {
  getRoleLabel,
  getRoleLabelPlural,
  getRelationshipRoles,
  getAllRelationshipTypes,
  isValidRelationshipType,
  getRelationshipTypeName,
  enrichUserWithRoleLabel,
  formatUserWithRole,
  ROLE_LABEL_MAPPINGS
};
